<template>
  <div class="new-study-set">
    <organization-breadcrumb
      :items="[$t('study_sets.curriculum'), $t('study_sets.study_set')]"
    />
    <div id="study-set-page">
      <el-row type="flex" justify="center">
        <el-col :xs="24" :md="14">
          <router-link
            :to="'/organization/' + organizationId + '/studysets'"
            style="font-size: 16px; font-weight: 600; line-height: 24px"
          >
            <i class="el-icon-back"></i> {{ $t("study_sets.back") }}
          </router-link>

          <div class="sub-header flex items-center justify-between">
            <rename-form
              v-if="editMode"
              :oldName="getCurrentStudySet.name"
              v-on:cancel-rename="cancelRename"
              v-on:handle-edit-name="handleEditName"
            >
            </rename-form>
            <p v-else class="study-set-name">
              {{ getCurrentStudySet.name }}
              <span
                class="edit-name"
                @click="() => (editMode = true)"
                :title="$t('assignments.details.edit_timeline')"
              >
                <i class="elsa-icon-edit2" />
              </span>
            </p>
            <div class="flex">
              <span
                id="assign-to-group-btn"
                class="assign-to-class"
                @click="handleAssignToClassStudySet()"
              >
                <chip>
                  {{
                    $t(
                      $i18nLabelsHelper(
                        currentOrganizationType,
                        `study_sets.assign_to_group`
                      )
                    )
                  }}
                </chip>
              </span>
              <el-popover
                class="study-set-popover"
                placement="bottom-end"
                trigger="click"
                :width="130"
                :visible-arrow="false"
              >
                <div class="actions-list">
                  <span @click="handleDuplicateStudySet(getCurrentStudySet.id)">
                    <span class="actions-list-item">
                      <font-awesome-icon icon="copy" />
                    </span>
                    {{ $t("study_sets.table.duplicate") }}
                  </span>
                  <span
                    @click="handleEditStudySet(getCurrentStudySet.id)"
                    :class="{
                      'btn-edit-disabled': canAccessStudySet(getCurrentStudySet.id, 'view')
                    }"
                    v-if="!isOrganizationK12"
                  >
                    <span class="actions-list-item">
                      <font-awesome-icon icon="pen" />
                    </span>
                    {{ $t("study_sets.table.edit") }}
                  </span>
                  <span
                    v-show="false"
                    class="remove-study-set"
                    @click="handleDeleteStudySet()"
                  >
                    <span class="actions-list-item">
                      <font-awesome-icon
                        :icon="['fas', 'trash-alt']"
                        style="
                          color: #eb5757;
                          font-size: 14px;
                          line-height: 14px;
                        "
                      />
                    </span>
                    {{ $t("study_sets.table.remove") }}
                  </span>
                </div>
                <el-tooltip
                  :visible-arrow="false"
                  class="item"
                  content="Options"
                  placement="top"
                  slot="reference"
                >
                  <el-button>
                    <i class="el-icon-more"></i>
                  </el-button>
                </el-tooltip>
              </el-popover>
            </div>
          </div>
          <div>
            <div class="study-set-main">
              <study-set-overview
                :currentStep="studySetSteps.active"
                :componentStep="2"
                v-on:next-step="handleNextStep"
                v-on:edit-step="handleEdit"
                v-on:cancel="handleCancel"
                :studysetDetails="buildingStudySet"
                :organizationType="currentOrganizationType"
                :uploadId.sync="uploadId"
                :organizationId="currentOrganizationId"
              />
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
    <create-study-set-success-modal
      :dialogVisible="modals.showCreateStudySetSuccessModal"
      v-on:closed="handleConfirmDoneCreateStudySet"
      v-on:new-set="handleCreateNewStudySet"
      v-on:assign-class="handleAssignStudySetToClass"
      :studySetName="membershipSelectedDisplayName"
      :countOfPhrases="countOfPhrases"
    />
    <leave-create-study-set-modal
      :dialogVisible="modals.showLeaveCreateStudySetModal"
      v-on:closed="modals.showAssignLicenseModal = false"
      v-on:leave="handleConfirmCancel"
    />
    <confirm-delete-study-set-modal
      :dialogVisible="modals.showConfirmDelete"
      append-to-body
      v-on:closed="modals.showConfirmDelete = false"
      v-on:delete="handleConfirmDeleteStudySet"
    >
      <template v-slot:body>
        <h4 id="leave-modal-title">
          {{
            $t(
              $i18nLabelsHelper(
                currentOrganizationType,
                `study_sets.modals_delete_title`
              )
            )
          }}
        </h4>
      </template>
    </confirm-delete-study-set-modal>
    <add-assignment-wrapper ref="addAssignment" v-on:reload="handleReload" />
  </div>
</template>

<script>
// import Steps from '@/components/common/Steps'
import StudySetOverview from "@/components/studysets/details/StudySetOverview";
import { mapGetters, mapActions } from "vuex";
//import { successMessage, warningMessage } from '@/utils/messages'
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import CreateStudySetSuccessModal from "@/components/modals/studysets/CreateStudySetSuccessModal";
import LeaveCreateStudySetModal from "@/components/modals/studysets/LeaveCreateStudySetModal";
//import { pathOr, isNil, compose, propOr, length } from 'ramda'
import Chip from "@/components/common/Chip";
import { successMessage, warningMessage } from "@/utils/messages";
import { DefaultShareableOptions } from "@/constants/studySets";
import { getOptionsLabels } from "@/utils/elementUi";
import ConfirmDeleteStudySetModal from "@/components/modals/bases/ConfirmDelete";
import AddAssignmentWrapper from "@/components/modals/studysets/AddAssignmentWrapper";
import RenameForm from "@/components/common/RenameForm";

export default {
  props: {
    studySetId: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    // Steps,
    StudySetOverview,
    OrganizationBreadcrumb,
    CreateStudySetSuccessModal,
    LeaveCreateStudySetModal,
    Chip,
    ConfirmDeleteStudySetModal,
    AddAssignmentWrapper,
    RenameForm,
  },
  computed: {
    ...mapGetters({
      getCurrentStudySet: "studysets/getCurrentStudySet",
      buildingStudySet: "studysets/getBuildingStudySet",
      currentOrganizationType: "organization/currentOrganizationType",
      currentOrganizationId: "organization/currentOrganizationId",
      isOrganizationK12: "organization/isOrganizationK12",
    }),
    isStudySetEditable() {
      if (
        this.buildingStudySet.assigned_group &&
        this.buildingStudySet.assigned_group.length > 0
      ) {
        return false;
      }
      return true;
    },
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    membershipSelectedDisplayName() {
      if (this.buildingStudySet && this.buildingStudySet.name) {
        return this.buildingStudySet.name;
      }
      return "";
    },
    countOfPhrases() {
      if (this.buildingStudySet && this.buildingStudySet.phrases) {
        return this.buildingStudySet.phrases.length;
      }
      return 0;
    },
    studySetDetails() {
      return this.buildingStudySet;
    },
  },
  data() {
    return {
      modals: {
        showInviteTeacherModal: false,
        showCreateStudySetSuccessModal: false,
        showLeaveCreateStudySetModal: false,
        showConfirmDelete: false,
      },
      uploadId: null,
      inviteMode: null,
      editMode: false,
      studySetSteps: {
        active: 2,
        steps: [
          {
            title: this.$t("studysets.create_studyset_flow.details"),
          },
          {
            title: this.$t("study_sets.review_confirm"),
          },
        ],
      },
      checkedStudySets: {
        view: [],
        edit: [],
      },
    };
  },
  methods: {
    ...mapActions({
      updateBuildingStudySet: "studysets/updateBuildingStudySet",
      deleteStudySetAction: "studysets/deleteStudySet",
      updateStudySetName: "studysets/updateStudySetName",
      checkStudySetPermissionAction: "studysets/checkStudySetPermission",
    }),
    handleAssignToClassStudySet() {
      this.$refs.addAssignment.handleAssignToClassStudySet(
        this.getCurrentStudySet
      );
    },
    handleDeleteStudySet() {
      this.modals.showConfirmDelete = true;
    },
    async handleDuplicateStudySet(studySetId) {
      // TODO duplicate study set
      return this.$router.push({
        name: "studyset-duplicate",
        params: {
          studySetId: studySetId,
        },
      });
    },
    async handleConfirmDeleteStudySet() {
      try {
        await this.deleteStudySetAction(this.studySetId);
        successMessage(this.$t("study_sets.delete_study_set_success"))(this);
        return this.$router.push({
          name: "studysets",
          params: {
            studySetId: this.studySetId,
          },
        });
      } catch (error) {
        return warningMessage(this.$t("study_sets.delete_study_set_warning"))(
          this
        );
      }
    },
    getShareableLabel(v) {
      return getOptionsLabels(DefaultShareableOptions, v);
    },
    clickContinute() {
      switch (this.studySetSteps.active) {
        case 1:
          this.$refs.studysetdetail.handleContinueClick();
          break;
        default:
          return;
      }
    },
    handleEditStudySet() {
      if (this.canAccessStudySet(this.studySetId, 'view')) {
        return warningMessage(
          this.$t("study_sets.edit_disabled_message"),
          'elsa-message info'
        )(this);
      }

      return this.$router.push({
        name: "studyset-edit",
        params: {
          studySetId: this.studySetId,
        },
      });
    },
    handleNextStep(activeStep) {
      // const activeStep = this.createStudySetSteps.active;
      this.studySetSteps.active = activeStep + 1;

      if (this.studySetSteps.active > 2) {
        this.modals.showCreateStudySetSuccessModal = true;
      }
    },
    handlePreviousStep(options = {}) {
      const activeStep = this.studySetSteps.active;
      this.studySetSteps.active = activeStep - 1;
    },
    handleEdit() {
      this.studySetSteps.active = 1;
      this.editMode = true;
    },
    handleAssignStudySetToClass() {
      this.$store.dispatch("studysets/resetBuildingStudySet");
      this.modals.showCreateStudySetSuccessModal = false;
      return this.$router.push({
        name: "studysets",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    handleConfirmDoneCreateStudySet() {
      this.$store.dispatch("studysets/resetBuildingStudySet");
      this.modals.showCreateStudySetSuccessModal = false;
      return this.$router.push({
        name: "studysets",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    handleCreateNewStudySet() {
      this.$store.dispatch("studysets/resetBuildingStudySet");
      this.studySetSteps.active = 1;
      this.editMode = true;
      this.modals.showCreateStudySetSuccessModal = false;
    },
    handleConfirmCancel() {
      // leave
      // this.createStudySetSteps.active = 1
      this.$store.dispatch("studysets/resetBuildingStudySet");
      return this.$router.push({
        name: "studysets",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    handleCancel() {
      this.modals.showLeaveCreateStudySetModal = true;
    },
    ...mapActions({
      createClassAction: "studysets/createStudySet",
    }),
    async handleReload() {
      try {
        this.handleCheckStudySetPermission(Number(this.studySetId));
        await this.$store.dispatch("studysets/resetBuildingStudySet");
        await this.$store.dispatch("studysets/getStudySet", {
          studySetId: this.studySetId,
        });
        const currentStudySet = this.getCurrentStudySet;
        const buildingStudySetData = {
          ...currentStudySet,
          phrases: currentStudySet.phrases.map((x) => {
            return { text: x };
          }),
        };
        this.updateBuildingStudySet(buildingStudySetData);
      } catch (error) {
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    async handleEditName(newName) {
      const payload = { name: newName };
      this.loading = true;
      this.editMode = false;
      try {
        await this.updateStudySetName({
          studySetId: this.studySetId,
          payload: payload,
        });
        this.getCurrentStudySet.name = newName;
        successMessage(this.$t("common.update_name_success"))(this);
      } catch (error) {
        return warningMessage(this.$t("common.generic_error_message"))(this);
      } finally {
        this.loading = false;
      }
    },
    cancelRename() {
      this.editMode = false;
    },
    canAccessStudySet(studySetId, action = "edit") {
      return this.checkedStudySets[action].includes(Number(studySetId));
    },
    async handleCheckStudySetPermission(studySetId) {
      try {
        this.checkedStudySets = await this.checkStudySetPermissionAction([
          studySetId,
        ]);
      } catch (error) {
        error;
      }
    },
  },
  async mounted() {
    await this.handleReload();
  },
  destroyed() {
    this.$store.dispatch("studysets/resetBuildingStudySet");
  },
};
</script>

<style lang="scss" scoped>
@import "../scss/_colors.scss";
.new-study-set {
  width: 100%;
}
.back-text {
  color: #007eff;
  font-weight: 600;
}
.study-set-name {
  font-size: 30px;
  line-height: 45px;
  font-weight: 700;
  margin-top: 0px;
  margin-bottom: 0px;
  display: flex;
}

.sub-header {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 16px;
}

#assign-to-group-btn ::v-deep {
  .el-button {
    padding: 8px 16px !important;
  }

  .elsa-chip {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }
}

.assign-to-class {
  margin-right: 8px;
}

.remove-study-set {
  color: red;
}

#study-set-page {
  margin: 8px;
  @media only screen and (max-width: 768px) {
    margin: 0 30px;
  }
  h1 {
    border: 0;
  }
  .study-set-create-steps {
    margin-top: 25px;
  }
  .study-set-main {
    background-color: white;
    padding: 0px;
  }
  ::v-deep .base-create-header {
    padding-bottom: 34px;
  }
  ::v-deep .circle-step {
    border: 1px solid #e6e6e6;
    box-sizing: border-box;
    border-radius: 23px;
    width: 40px;
    height: 40px;
    display: inline-flex;
    justify-content: center;
    padding-top: 8px;
    margin-right: 16px;
  }
}

.success-info {
  .success {
    font-weight: bold;
    font-size: 22px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #46c779;
    line-height: 30px;
  }
  p {
    margin-top: 20px;
    font-weight: 300;
    font-size: 18px;
    color: #546873;
    margin: 0;
  }
}
::v-deep .create-study-set-com-disable {
  opacity: 0.5;
}
.line-separate {
  border: 1px solid#E6E6E6;
  margin-bottom: 32px;
}
.edit-name {
  color: #478dff;
  cursor: pointer;
  padding: 0px 13px;
  margin-top: 6px;
}
.actions-list-item {
  width: 24px;
  height: 24px;
  padding-right: 8px;
}
.btn-edit-disabled {
  color: #c0c4cc !important;
  background-image: none;
  background-color: #fff;
  border-color: #ebeef5;

  svg {
    color: #c0c4cc !important;
  }
}
#leave-modal-title {
  font-weight: 700;
  font-size: 22px;
  line-height: 32px;
  text-align: center;
  color: $black;
  margin: 0 auto;
}
</style>
